import React, { useState } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';

import { Alert, Button } from '@mui/material';

import { PRODUCTS } from '~/consts/products';

import { queueItemsRefundedReportV2 } from '~/services/ReportingService';
import { addMonths, formatDateISO, startOfMonth } from '~/services/TimeService';

import ErrorDisplay from '../ErrorDisplay';

function validateForm(formData, errors) {
  if (typeof formData.endAt === 'undefined') {
    errors.endAt.addError('Please enter end at');
  }
  if (typeof formData.startAt === 'undefined') {
    errors.startAt.addError('Please enter start at');
  }
  if (typeof formData.product === 'undefined') {
    errors.product.addError('Please select a product');
  }

  return errors;
}

const formSchema: RJSFSchema = {
  description: 'Please choose time period for export',
  type: 'object',
  properties: {
    startAt: {
      title: 'Select Start Date',
      type: 'string',
      format: 'date',
      default: formatDateISO(addMonths(-1, startOfMonth())),
    },
    endAt: {
      title: 'Select End Date',
      type: 'string',
      format: 'date',
      default: formatDateISO(addMonths(0, startOfMonth())),
    },
    product: {
      title: 'Product',
      type: 'string',
      oneOf: (Object.entries(PRODUCTS) as [keyof typeof PRODUCTS, string][]).map(([key, label]) => ({
        const: key,
        title: label,
      })),
    },
    orderType: {
      title: 'Order Type',
      type: 'string',
      anyOf: [
        {
          type: 'string',
          enum: ['standard'],
          title: 'Standard',
        },
        {
          type: 'string',
          enum: ['with_test'],
          title: 'With Test Orders',
        },
        {
          type: 'string',
          enum: ['with_test_and_failed'],
          title: 'With Test and Failed Orders',
        },
      ],
    },
  },
};

const uiSchema = {
  startAt: {
    'ui:widget': 'date',
  },
  endAt: {
    'ui:widget': 'date',
  },
  orderType: {
    'ui:widget': 'radio',
    'ui:options': {
      inline: true,
    },
  },
};

const formData = {
  product: 'all',
};

export default function ExportRefundsV2Form() {
  const [successMessage, setSuccessMessage] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = (event: IChangeEvent) => {
    queueItemsRefundedReportV2({
      startAt: event.formData.startAt,
      endAt: event.formData.endAt,
      product: event.formData.product,
      orderType: event.formData.orderType,
    })
      .then(function () {
        setSuccessMessage(true);
      })
      .catch(function (err) {
        setError(err.message);
      });
  };

  return (
    <Form
      schema={formSchema}
      uiSchema={uiSchema}
      onSubmit={handleSubmit}
      customValidate={validateForm}
      formData={formData}
      validator={validator}
      showErrorList={false}
    >
      <Button type="submit" variant="contained">
        Email Items Refunded Report
      </Button>

      {error && <ErrorDisplay message={error} />}

      {successMessage && (
        <Alert severity="success">
          <p>Your report is queued for delivery and will be emailed shortly.</p>
        </Alert>
      )}
    </Form>
  );
}
